/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { Helmet } from 'react-helmet-async';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { GlobalStyle } from 'styles/global-styles';

import { translations } from 'locales/i18n';
import { useTranslation } from 'react-i18next';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import { ProtectedRoute } from './components/ProtectedRoute/Loadable';
import { AdminPage } from './containers/AdminPage/Loadable';
import { LogsPage } from './containers/AdminPage/LogsPage/Loadable';
import { Header } from './containers/Header/Loadable';
import { HomePage } from './containers/HomePage/Loadable';
import { LoginPage } from './containers/LoginPage/Loadable';
import { UnauthorizedPage } from './containers/UnauthorizedPage';

import { HotKeys } from '@/utils/hotKeyHandler';
import {
  AppInsightsErrorBoundary,
  ReactPlugin,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
  HotKeysParentProvider,
  HotKeysProvider,
  TestIdContext,
} from '@mti/multitenant-ui-shared';
import { useRegisterAuthFunctions } from 'hooks/useRegisterAuthFunctions';
import { useSelector } from 'react-redux';
import { GoogleMapsApi } from './components/GoogleMapsApi';
import { selectUserProfile } from './containers/AuthProvider/selectors';

// Initialize Application Insights
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString:
      process.env.REACT_APP_APPLICATION_INSIGHTS_CONNECTION_STRING ||
      'InstrumentationKey=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxxx',
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
  },
});
appInsights.addTelemetryInitializer(envelope => {
  envelope.tags = envelope.tags || {};
  envelope.tags['ai.device.roleName'] = 'ui-container';
  envelope.tags['ai.device.roleInstance'] =
    process.env.REACT_APP_APPLICATION_INSIGHTS_INSTANCE;
});
appInsights.loadAppInsights();

export function App() {
  const { t } = useTranslation();
  const userProfile = useSelector(selectUserProfile);

  useRegisterAuthFunctions();
  appInsights.setAuthenticatedUserContext(userProfile?.id);

  return (
    <AppInsightsErrorBoundary
      onError={() => <p>{t(translations.App.Error)}</p>}
      appInsights={reactPlugin}
    >
      <TestIdContext.Provider value={{ parent: 'uicontainer' }}>
        <BrowserRouter>
          <Helmet titleTemplate={'%s'} defaultTitle={t(translations.App.Title)}>
            <meta name="description" content="Multi-Tenant Portal" />
          </Helmet>
          {userProfile.mapProvider?.browserKey && <GoogleMapsApi />}
          <HotKeysParentProvider>
            <HotKeysProvider name={'container'}>
              <HotKeys />
              <Header></Header>
              <Routes>
                <Route path="/" element={<LoginPage />} />
                <Route path="/:tenantId/login" element={<LoginPage />} />
                <Route path="/admin/login" element={<LoginPage />} />
                <Route path="/unauth" element={<UnauthorizedPage />} />
                <Route
                  path="/logs"
                  element={
                    <ProtectedRoute>
                      <LogsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/home"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId/admin"
                  element={
                    <ProtectedRoute>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId/book"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId/dispatch"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId/map"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/:tenantId/home"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              <GlobalStyle />
            </HotKeysProvider>
          </HotKeysParentProvider>
        </BrowserRouter>
      </TestIdContext.Provider>
    </AppInsightsErrorBoundary>
  );
}
